import { type IkeClientService, Services } from '../';
import type { Code } from './code.enum';
import { InputEventBindContext } from './event.context';
import { InputEventInfo } from './event.info';

export type Modifiers = {
	ctrl: boolean;
	shift: boolean;
	alt: boolean;
	meta: boolean;
};

export type EventDescriptor = {
	code: Code | undefined;
	modifiers: Modifiers;
	eventType: 'keydown' | 'keyup';
};

export type EventData = {
	event: KeyboardEvent;
	descriptor: EventDescriptor;
};

export class InputService implements IkeClientService {
	__priority = 100;
	isDebug = true;

	private currentModifiers: Modifiers = { ctrl: false, shift: false, alt: false, meta: false };
	private events: Map<symbol, InputEventInfo> = new Map();
	private bound: boolean = false;

	init() {
		if (this.bound) {
			return;
		}
		this.bound = true;

		const $window = Services.Window.window;
		$window.document.addEventListener('keydown', this.onKeyDown.bind(this));
		$window.document.addEventListener('keyup', this.onKeyUp.bind(this));
		$window.addEventListener('focus', this.onWindowFocus.bind(this));
	}

	getContext() {
		return new InputEventBindContext(this);
	}

	onKeyDown(evt: KeyboardEvent) {
		const code = evt.code as Code;
		this.setModifiers(evt);

		const eventDescriptor: EventDescriptor = {
			code,
			modifiers: this.currentModifiers,
			eventType: 'keydown',
		};

		this.emit(eventDescriptor, evt);
	}

	onKeyUp(evt: KeyboardEvent) {
		const code = evt.code as Code;
		this.setModifiers(evt);

		const eventDescriptor: EventDescriptor = {
			code,
			modifiers: this.currentModifiers,
			eventType: 'keyup',
		};

		this.emit(eventDescriptor, evt);
	}

	/** Reset the current modifiers on window focus */
	onWindowFocus() {
		this.currentModifiers = { ctrl: false, shift: false, alt: false, meta: false };
	}

	on(ctx: InputEventBindContext, eventDescriptor: EventDescriptor, callback: (data: EventData) => void) {
		const eventSymbol = Symbol();
		this.events.set(eventSymbol, new InputEventInfo(eventDescriptor, callback));
		return eventSymbol;
	}

	off(ctx: InputEventBindContext, symbol: symbol) {
		return this.events.delete(symbol);
	}

	private emit(eventDescriptor: EventDescriptor, evt: KeyboardEvent) {
		this.events.forEach((event) => {
			if (event.shouldTrigger(eventDescriptor)) {
				const eventData: EventData = {
					event: evt,
					descriptor: eventDescriptor,
				};
				event.callback(eventData);
			}
		});
	}

	private setModifiers(evt: KeyboardEvent) {
		this.currentModifiers = {
			ctrl: evt.ctrlKey,
			shift: evt.shiftKey,
			alt: evt.altKey,
			meta: evt.metaKey,
		};
	}
}
